/**
 * La caja para contestar con texto libre dentro de las 24 h (§7 y §15.3).
 *
 * La ventana se calcula contra `contestarHasta` (el backend corta 60 s antes de las 24 h) y con `ahora`
 * corregido por el desfase con el servidor: con `+24 h` y el reloj de la PC, la caja decía «hasta 14:32»
 * en el último minuto y el envío volvía rechazado.
 *
 * Lo que NO se puede perder:
 * - El borrador. Si la ventana se cierra mientras se escribe, el texto queda a la vista en solo lectura
 *   (antes el textarea se desmontaba y se perdía), y un entrante nuevo reabre la caja con el texto. Lo mismo
 *   con un bloqueo (`bloqueoEnvio`) que llega después de escribir: queda con «Copiar» junto al aviso.
 * - Un mensaje por intento. La clave del envío va atada al texto: el MISMO texto usa la MISMA clave hasta
 *   un `ok:true` (también ante un error de red o un 5xx, que pudieron haber llegado al backend: con clave
 *   nueva salía dos veces); un texto distinto, clave nueva (con la vieja, el backend devolvía el primer
 *   envío como `repetido` y el texto corregido no salía, sin aviso).
 * - Lo que vio el operador. `vistoHasta` sigue a la última salida mientras la caja está vacía y se congela
 *   cuando empieza a escribir: si otra persona del equipo contesta en el medio, el backend responde 409.
 *
 * Todo envío pasa por `llamar()` (R15): un 422 vuelve RESUELTO, y limpiar la caja sin mirar `ok` pintaba
 * como enviado algo que el backend rechazó. El texto se borra SÓLO con `ok:true`.
 */

import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Loader2, Send } from "lucide-react";
import { conversacionesApi } from "../../conversacionesApi";
import type { ConversacionDetalle, EnvioManualPublico, ItemHilo } from "../../types.conversaciones";
import { haceCuanto, llamar } from "../base/comun";
import { textoError } from "../../logic/respuesta";
import { nombreDeAutorPara } from "../../logic/formato";
import { useEsEquipoDeenex } from "../../../campana-nueva/hooks/useEsEquipoDeenex";
import {
  MAX_TEXTO_RESPUESTA, capitalizar, conTecladoFisico, esComputadora, esPantallaBaja, excedente, faltaPoco, insertarRapida, nuevaClaveCliente, textoCosto,
  textoCostoCorto, textoQuedanMinutos, textoRestante, textoVentanaLargo, ultimoManualEnCurso,
} from "../../logic/conversaciones";

/** Un envío que quedó así no le llegó a la persona: no cuenta como «lo último que salió». */
const NO_SALIO = new Set(["fallido", "no_entregado", "cancelado"]);

/** Lo que se lee abajo de la caja después de un envío. Con `motivo`, el envío no le llegó a la persona. */
type Info = { texto: string; motivo: string };

/**
 * `repetido`: esa clave ya tenía un envío y el backend devuelve ÉSE. Si había fallado, «ya había salido» era mentira:
 * el operador daba por contestada a alguien que no recibió nada. El motivo es el del envío; un cancelado no trae
 * `error` (el backend lo arma sólo para fallido y no entregado).
 */
function infoRepetido(e: EnvioManualPublico, equipo: boolean): Info {
  if (!NO_SALIO.has(e.estado)) return { texto: "Esa respuesta ya había salido.", motivo: "" };
  // el código de error es del equipo (lo busca en la documentación de WhatsApp); a un cliente no le dice nada
  const motivo = e.error
    ? `${e.error.titulo}. ${e.error.texto}`
    : e.estado === "cancelado" ? "Se canceló antes de salir."
      : e.codigoError ? (equipo ? `WhatsApp no lo aceptó (código ${e.codigoError}).` : "WhatsApp no lo aceptó.") : "No sabemos por qué.";
  return { texto: "Esa respuesta ya se había intentado y no salió.", motivo };
}

type Otro = { nombre: string; at: string; texto: string };

function leerOtro(x: unknown): Otro {
  const o = (x && typeof x === "object" ? x : {}) as Record<string, unknown>;
  return {
    nombre: typeof o.nombre === "string" && o.nombre ? o.nombre : "otra persona",
    at: typeof o.at === "string" ? o.at : "",
    texto: typeof o.texto === "string" ? o.texto : "",
  };
}

// Desde `xl` la caja va en la misma columna que la ficha y el hilo, con el alto de la pantalla: cada línea que se ahorra
// acá es hilo que se ve (a 1280x720, con la caja de antes, al hilo le quedaban 128 px).
const cajaCls = "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm text-sm text-slate-700 xl:p-3";
const botonCls = "h-9 px-4 rounded-xl text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50";

export function CajaRespuesta({ conversacion: c, items, ahora, onEnviado, onRechazo, onReabrir, textoParaCargar }: {
  conversacion: ConversacionDetalle;
  /** El hilo ya ordenado. */
  items: ItemHilo[];
  /** El reloj corregido por el desfase con el servidor. */
  ahora: Date;
  onEnviado: (e: EnvioManualPublico) => void;
  /** Un rechazo (403, 404, 409, 422): se vuelve a pedir el hilo, que es la fuente de verdad. */
  onRechazo: () => void;
  onReabrir: () => void;
  /** «Volver a intentar» de una burbuja: carga su texto en la caja. */
  textoParaCargar?: { texto: string; n: number } | null;
}) {
  // Quién respondió mientras tanto: a una persona de un cliente, alguien del equipo es «Equipo Deenex» (`nombreDeAutorPara`).
  const equipo = useEsEquipoDeenex();
  const idTexto = useId();
  const [texto, setTexto] = useState("");
  const [visto, setVisto] = useState(c.ultimaSalidaId);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState<Info | null>(null);
  const [otro, setOtro] = useState<Otro | null>(null);
  const [copiado, setCopiado] = useState(false);
  // En el celular la caja va fija abajo y con todo a la vista medía 418 px de 755 (a 375x812): quedaban 2 burbujas. La
  // ventana, el detalle del costo y los links se pliegan EN LA PÁGINA; desde `xl` van desplegados, salvo con poco alto
  // (`esPantallaBaja`), donde le quitaban al hilo lo mismo que en el celular.
  const [desplegada] = useState(() => esComputadora() && !esPantallaBaja());
  // «Ctrl + Enter» sólo donde hay Ctrl: en el celular el aviso no servía y ocupaba la fila de «Enviar».
  const [conTeclado] = useState(conTecladoFisico);
  const [detalles, setDetalles] = useState(false);
  const textoRef = useRef(texto);
  textoRef.current = texto;
  const claveRef = useRef<{ texto: string; clave: string } | null>(null);
  const enviandoRef = useRef(false);
  const vivo = useRef(true);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);

  // Con la caja vacía, `vistoHasta` sigue a la última salida; con texto, queda congelado.
  useEffect(() => {
    if (!textoRef.current) setVisto(c.ultimaSalidaId);
  }, [c.ultimaSalidaId]);

  useEffect(() => {
    if (!textoParaCargar) return;
    setTexto(textoParaCargar.texto);
    setError("");
    setInfo(null);
    setOtro(null);
    areaRef.current?.focus();
  }, [textoParaCargar]);

  const cambiarTexto = (v: string) => {
    setTexto(v);
    setError("");
    setInfo(null);
    // La tarjeta del 409 es sobre ESTE texto: si cambia, «Mandar igual» mandaría otro con la clave vieja.
    setOtro(null);
  };

  const t = texto.trim();
  const sobra = excedente(texto);
  const enBaja = (c.baja.enNoContactar || c.estado === "baja") && !c.baja.puedeContestar;
  const vencida = ahora.getTime() >= Date.parse(c.contestarHasta);
  const cerrada = !c.ventanaAbierta || !!c.otroNumero || vencida;

  const enviar = async (confirmarDuplicado = false) => {
    if (enviandoRef.current || !t || sobra > 0 || cerrada) return;
    const clave = claveRef.current?.texto === t ? claveRef.current.clave : nuevaClaveCliente();
    claveRef.current = { texto: t, clave };
    enviandoRef.current = true;
    setEnviando(true);
    setError("");
    setInfo(null);
    const l = await llamar<EnvioManualPublico>(() => conversacionesApi.responder(c.id, {
      texto: t, claveCliente: clave, vistoHasta: visto, ...(confirmarDuplicado ? { confirmarDuplicado: true } : {}),
    }));
    enviandoRef.current = false;
    if (!vivo.current) return;
    setEnviando(false);
    if (l.ok) {
      // Esa clave ya se gastó, aunque el envío haya quedado fallido: el próximo intento lleva otra.
      claveRef.current = null;
      setOtro(null);
      setTexto("");
      if (l.data?.repetido) setInfo(infoRepetido(l.data, equipo));
      if (l.data && !NO_SALIO.has(l.data.estado)) setVisto(l.data.id);
      if (l.data) onEnviado(l.data);
      return;
    }
    if (l.code === "OTRO_OPERADOR_RESPONDIO") { setOtro(leerOtro(l.extra.otro)); return; }
    setError(`No se mandó: ${textoError(l)}`);
    if ([403, 404, 409, 422].includes(l.status)) onRechazo();
  };

  const copiar = async () => {
    try { await navigator.clipboard?.writeText(texto); setCopiado(true); } catch { /* sin portapapeles: el texto sigue a la vista */ }
  };

  const errorLinea = error ? <p role="alert" className="text-red-700">{error}</p> : null;

  if (c.anonimizada) return null;
  if (!c.puedeContestar) {
    return <div className={cajaCls}><p>{c.motivoNoPuedeContestar || "No podés contestar esta conversación."}</p></div>;
  }
  if (c.bloqueoEnvio) {
    // El bloqueo también llega DESPUÉS de escribir: un 422 de saldo o de tope al mandar vuelve a pedir el hilo, que ya
    // lo trae, y una campaña puede gastar el saldo mientras se escribe. El borrador queda a la vista para copiarlo antes
    // de ir a Configuración (que desmonta la página y lo pierde), con el «No se mandó». Si el bloqueo se va (se cargó
    // saldo), la caja vuelve sola con el texto: el estado nunca se borró.
    return (
      <div className={`${cajaCls} space-y-2 border-amber-200 bg-amber-50 text-amber-900`}>
        <p>{c.bloqueoEnvio.texto}</p>
        {texto && (
          <>
            <label htmlFor={idTexto} className="sr-only">Tu respuesta</label>
            <textarea id={idTexto} ref={areaRef} value={texto} readOnly rows={3}
              className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-slate-600" />
            <p>
              Tu texto quedó acá (
              <button type="button" onClick={() => void copiar()} className="font-semibold underline">{copiado ? "Copiado" : "Copiar"}</button>
              ). Copialo antes de salir de la conversación: al volver, la caja empieza vacía.
            </p>
          </>
        )}
        {errorLinea}
        {c.bloqueoEnvio.enlace && <Link to={c.bloqueoEnvio.enlace} className="inline-block font-semibold underline">Ir a Configuración</Link>}
      </div>
    );
  }
  if (enBaja) {
    return <div className={cajaCls}><p>No se le puede contestar: pidió no recibir mensajes y no volvió a escribir después.</p></div>;
  }

  if (cerrada) {
    return (
      <div className={`${cajaCls} space-y-2`}>
        <p>
          {/* Cerrada con tiempo por delante es que WhatsApp ya rechazó una respuesta por ventana (63016): «pasaron más de
              24 h» era falso, y el operador veía que le había escrito hace un rato. */}
          {c.otroNumero
            ? "Te escribió a un número que ya no es el de la marca: desde el actual sólo se le puede escribir con una plantilla aprobada."
            : vencida
              ? "Pasaron más de 24 h desde su último mensaje. WhatsApp sólo deja escribirle con una plantilla aprobada."
              : "WhatsApp dice que la ventana de 24 h de esta persona está cerrada: sólo se le puede escribir con una plantilla aprobada."}
        </p>
        {texto && (
          <>
            <label htmlFor={idTexto} className="sr-only">Tu respuesta</label>
            <textarea id={idTexto} ref={areaRef} value={texto} readOnly rows={3}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600" />
            <p className="text-amber-800">
              Se cerró la ventana mientras escribías. Tu texto quedó acá (
              <button type="button" onClick={() => void copiar()} className="font-semibold underline">{copiado ? "Copiado" : "Copiar"}</button>
              ). Para escribirle, usá «Reabrir con plantilla».
            </p>
          </>
        )}
        {errorLinea}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onReabrir} className={`${botonCls} bg-violet-600 text-white hover:bg-violet-700`}>Reabrir con plantilla</button>
          {texto && <button type="button" disabled className={`${botonCls} border border-slate-200 bg-white`}>Enviar</button>}
        </div>
      </div>
    );
  }

  const usuario = c.contestarA?.tipo === "usuario";
  const ultima = c.ultimaSalidaId && c.ultimaSalidaId !== visto && t
    ? items.find((i) => i.id === `e:${c.ultimaSalidaId}`)
    : undefined;
  // Sólo si la última salida es POSTERIOR a lo que vio el operador. Si su propia respuesta no llega, el backend vuelve a
  // dar como última una anterior, y distinta de `visto` avisaba «Mientras escribías le respondió…» con un mensaje viejo.
  const vistoItem = ultima ? items.find((i) => i.id === `e:${visto}`) : undefined;
  const hayOtraSalida = !!(c.ultimaSalidaId && c.ultimaSalidaId !== visto && t) && !(ultima && vistoItem && ultima.clave <= vistoItem.clave);
  const poco = faltaPoco(c.contestarHasta, ahora);

  return (
    <div className={`${cajaCls} space-y-2 xl:space-y-1.5`}>
      {hayOtraSalida && (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-amber-800">
          {ultima && ultima.tipo === "manual"
            ? <>Mientras escribías le respondió {nombreDeAutorPara(ultima.autor, equipo)}: «{ultima.texto}»</>
            : <>Mientras escribías le respondió otra persona.</>}
        </p>
      )}
      {otro && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900">
          <p>
            {capitalizar(haceCuanto(otro.at, ahora)) || "Recién"} le respondió {otro.nombre}{otro.texto ? <>: «{otro.texto}»</> : null}. ¿Mandás igual tu respuesta?
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => void enviar(true)} disabled={enviando} className={`${botonCls} bg-amber-600 text-white`}>Mandar igual</button>
            <button type="button" onClick={() => setOtro(null)} disabled={enviando} className={`${botonCls} border border-amber-200 bg-white`}>No mandar</button>
          </div>
        </div>
      )}

      {usuario ? (
        <>
          <p>Se le contesta a su usuario de WhatsApp (<span className="whitespace-nowrap">{c.contestarA?.valor}</span>)</p>
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-amber-800">
            Escribió sin número de teléfono. Contestarle depende de que Meta lo tenga habilitado en su país: si no llega, lo vas a ver en el mensaje.
          </p>
        </>
      ) : (
        <p>Se le contesta a: <span className="font-mono whitespace-nowrap">{c.contestarA?.valor || c.numero}</span></p>
      )}

      <label htmlFor={idTexto} className="sr-only">Tu respuesta</label>
      <textarea id={idTexto} ref={areaRef} value={texto} rows={3} maxLength={MAX_TEXTO_RESPUESTA}
        placeholder="Escribí tu respuesta…" onChange={(e) => cambiarTexto(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void enviar(); } }}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />

      {/* Lo que pide apurarse queda siempre fuera del pliegue. */}
      {poco && <p className="text-amber-700">{textoQuedanMinutos(c.contestarHasta, ahora)}</p>}
      {!desplegada && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {/* Lo que cuesta ESTA respuesta se ve siempre: la plata no se esconde detrás de un toque. */}
          {!detalles && <span className="text-slate-500">{textoCostoCorto(c.costoRespuesta)}</span>}
          <button type="button" onClick={() => setDetalles((x) => !x)} aria-expanded={detalles}
            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900">
            {detalles ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Ver ventana y costo
          </button>
        </div>
      )}
      {(desplegada || detalles) && (
        <>
          {!poco && (
            <p className="text-slate-500 xl:text-xs">
              {`Podés contestar con texto libre hasta ${textoVentanaLargo(c.contestarHasta, ahora)} (faltan ${textoRestante(c.contestarHasta, ahora)}).`}
            </p>
          )}
          <p className="text-slate-500 xl:text-xs">{textoCosto(c.costoRespuesta)}</p>
          {c.linksSinAcortar && <p className="text-slate-500 xl:text-xs">Los links que mandes desde acá no se acortan ni se mide el clic.</p>}
        </>
      )}

      {/* En una sola fila con scroll horizontal: en el celular, dos filas de chips eran más alto fijo tapando el hilo.
          Sin rápidas, «Cargar respuestas rápidas» lleva a Configuración → Conversaciones, que es del equipo: a una persona
          de un cliente (Accesos, rol «comercio») la mandaba a una sección que no puede abrir (caía en «Saldo y costos»).
          A ella, sin rápidas, no se le muestra la fila. */}
      {(c.respuestasRapidas.length > 0 || equipo) && (
        <div className="flex flex-nowrap items-center gap-1.5 overflow-x-auto xl:flex-wrap">
          <span className="shrink-0 text-xs text-slate-500">Respuestas rápidas:</span>
          {c.respuestasRapidas.length ? c.respuestasRapidas.map((r) => (
            <button key={r.id} type="button" onClick={() => cambiarTexto(insertarRapida(textoRef.current, r.texto))}
              className="shrink-0 whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
              {r.titulo}
            </button>
          )) : (
            <Link to="/activa/oficial/configuracion?seccion=conversaciones" className="shrink-0 whitespace-nowrap text-xs font-semibold text-violet-700 underline">Cargar respuestas rápidas</Link>
          )}
        </div>
      )}

      {ultimoManualEnCurso(items) && (
        <p className="text-amber-700">Tu respuesta anterior todavía se está confirmando: esperá antes de mandar otra, así no le llega dos veces.</p>
      )}
      {errorLinea}
      {info && (info.motivo ? (
        <div role="status" className="rounded-xl bg-amber-50 px-3 py-2 text-amber-800">
          <p className="font-semibold">{info.texto}</p>
          <p>Motivo: {info.motivo}</p>
        </div>
      ) : <p role="status" className="text-emerald-700">{info.texto}</p>)}

      {/* El contador va en la fila de «Enviar»: en una fila propia era un renglón más de alto fijo, y al pasarse explica
          al lado del botón por qué no deja mandar. */}
      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
        <span className="mr-auto flex flex-wrap gap-x-2 text-xs">
          <span className={sobra > 0 ? "font-semibold text-red-700" : "text-slate-400"}>{texto.length}/{MAX_TEXTO_RESPUESTA}</span>
          {sobra > 0 && <span className="font-semibold text-red-700">Te pasaste por {sobra} caracteres</span>}
        </span>
        {conTeclado && <span className="text-xs text-slate-400">Ctrl + Enter para enviar</span>}
        <button type="button" onClick={() => void enviar()} disabled={enviando || !t || sobra > 0}
          className={`${botonCls} bg-violet-600 text-white hover:bg-violet-700`}>
          {enviando ? <><Loader2 size={14} className="animate-spin" /> Enviando…</> : <><Send size={14} /> Enviar</>}
        </button>
      </div>
    </div>
  );
}

export default CajaRespuesta;
